import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import ArchDiagram from "@/components/ArchDiagram";
import TerminalSnippet from "@/components/TerminalSnippet";
import { ENGINES, BLOCKCHAIN_USES, ROADMAP, IMAGES } from "@/data/content";

export default function Platform() {
  const navigate = useNavigate();

  return (
    <main data-testid="platform-page" className="pt-32">
      <section className="grid-bg border-b border-zinc-900/10 pb-20">
        <div className="mx-auto max-w-7xl px-6">
          <SectionLabel>Platform Architecture</SectionLabel>
          <MaskedLines
            className="mt-6"
            lines={["Modular by design.", <span key="a" className="text-cyan-600">Verifiable by default.</span>]}
            lineClassName="font-heading text-5xl font-bold tracking-tighter leading-[1.05] text-zinc-900 sm:text-6xl lg:text-7xl"
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }} className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-600">
            Six engines share one orchestration core. Every product composes them differently — and every output flows through the blockchain trust layer.
          </motion.p>
        </div>
      </section>

      {/* DIAGRAM */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <Reveal><ArchDiagram /></Reveal>
      </section>

      {/* ENGINES DETAIL */}
      <section className="border-y border-zinc-900/10 bg-[#F0F2F5] py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>The Six Engines</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">Composable intelligence.</h2>
          </Reveal>
          <div className="mt-16 border-t border-zinc-900/10">
            {ENGINES.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.05}>
                <div data-testid={`platform-engine-${e.id}`} className="group grid grid-cols-1 gap-4 border-b border-zinc-900/10 py-10 transition-colors duration-300 hover:bg-zinc-900/[0.03] md:grid-cols-12 md:items-center">
                  <span className="font-mono text-xs text-zinc-400 md:col-span-2">{e.code}</span>
                  <h3 className="font-heading text-2xl font-semibold text-zinc-900 transition-colors duration-300 group-hover:text-cyan-600 md:col-span-3">{e.name}</h3>
                  <p className="text-sm leading-relaxed text-zinc-600 md:col-span-5">{e.description}</p>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-600/70 md:col-span-2 md:text-right">{e.spec}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCKCHAIN USE CASES */}
      <section className="relative overflow-hidden py-32">
        <div className="pointer-events-none absolute inset-0">
          <img src={IMAGES.blockchain} alt="" className="h-full w-full object-cover opacity-[0.05]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F7F8FA] via-transparent to-[#F7F8FA]" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>Blockchain Integration</SectionLabel>
            <h2 className="mt-6 max-w-2xl font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">
              Trust that survives <span className="text-cyan-600">the audit.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
            {BLOCKCHAIN_USES.map((u, i) => (
              <Reveal key={u.num} delay={i * 0.08}>
                <div data-testid={`blockchain-use-${u.num}`} className="card-hover h-full border border-zinc-900/10 bg-white/70 p-10 backdrop-blur-md">
                  <span className="font-mono text-sm text-blue-600">/{u.num}</span>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-zinc-900">{u.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-600">{u.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-16 grid grid-cols-1 items-center gap-10 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <p className="label-mono">Proof, in practice</p>
                <h3 className="mt-4 font-heading text-2xl font-semibold text-zinc-900 sm:text-3xl">Anchoring a decision takes one command.</h3>
                <p className="mt-4 text-sm leading-relaxed text-zinc-600">
                  When the Compliance Assistant approves a claim or flags a contract, the Verify engine hashes the full decision context and anchors it on-chain. Anyone — auditor, regulator, court — can recompute the proof without touching your data.
                </p>
              </div>
              <div className="lg:col-span-3"><TerminalSnippet /></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="border-t border-zinc-900/10 bg-[#F0F2F5] py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>Phased Roadmap</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">Shipping in sequence.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px border border-zinc-900/10 bg-zinc-900/10 md:grid-cols-2 lg:grid-cols-4">
            {ROADMAP.map((r, i) => (
              <Reveal key={r.phase} delay={i * 0.08}>
                <div data-testid={`roadmap-${r.phase.replace(" ", "-").toLowerCase()}`} className={`h-full p-8 ${r.status === "active" ? "bg-cyan-500/[0.06]" : "bg-[#F0F2F5]"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-mono text-[10px] tracking-[0.25em] ${r.status === "active" ? "text-cyan-600" : "text-zinc-400"}`}>{r.phase}</span>
                    {r.status === "active" && <span className="h-2 w-2 bg-cyan-500 pulse-node" />}
                  </div>
                  <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-zinc-400">{r.period}</p>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-zinc-900">{r.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {r.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-zinc-600">
                        <span className="mt-1.5 h-1 w-1 shrink-0 bg-cyan-500/60" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <button data-testid="platform-cta-demo-btn" onClick={() => navigate("/contact")} className="btn-primary mt-16">
              Discuss Your Deployment <ArrowUpRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
